import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, TextField, Typography, Button } from "@material-ui/core";
import clsx from "clsx";
import { Controller, useFormContext } from "react-hook-form";
import { EMAIL_TAGS } from "./emailTags";
import { useEmailTemplateStyles } from "./styles";
import {
  valueToHtml,
  htmlToValue,
  buildChipHtml,
  placeCursorAtEnd,
  placeCursorAtNode,
  removeDuplicateChips,
  insertLineBreak,
  getAdjacentChip,
  removeChipAndPlaceCursor,
} from "../../utils/emailTemplateEditor/emailTemplateEditorUtils";

const TemplateBodyField = React.forwardRef(({ value, onChange, label, testId }, ref) => {
  const classes = useEmailTemplateStyles();
  const editorRef = useRef(null);
  const internalChange = useRef(false);
  const [focused, setFocused] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    getEditorElement: () => editorRef.current,
    markInternalChange: () => { internalChange.current = true; },
  }));

  useEffect(() => {
    if (internalChange.current) {
      internalChange.current = false;
      return;
    }
    if (editorRef.current) {
      editorRef.current.innerHTML = valueToHtml(value || "");
      placeCursorAtEnd(editorRef.current);
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    removeDuplicateChips(editorRef.current);
    internalChange.current = true;
    const val = htmlToValue(editorRef.current).replace(/^\n+$/, "");
    onChange(val);
  }, [onChange]);

  const handleMouseDown = useCallback(
    (e) => {
      const deleteBtn = e.target.closest(".email-tag-chip-delete");
      if (!deleteBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const chip = deleteBtn.closest(".email-tag-chip");
      if (chip && editorRef.current) {
        const next = chip.nextSibling;
        chip.parentNode.removeChild(chip);
        placeCursorAtNode(next, editorRef.current);
        editorRef.current.focus();
        emitChange();
      }
    },
    [emitChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (insertLineBreak()) emitChange();
        return;
      }
      if (e.key === "Backspace") {
        const chip = getAdjacentChip("before");
        if (chip) {
          e.preventDefault();
          removeChipAndPlaceCursor(chip);
          emitChange();
        }
      }
      if (e.key === "Delete") {
        const chip = getAdjacentChip("after");
        if (chip) {
          e.preventDefault();
          removeChipAndPlaceCursor(chip);
          emitChange();
        }
      }
    },
    [emitChange]
  );

  const handleContainerClick = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  const handleDragStart = useCallback((e) => {
    const chip = e.target.closest(".email-tag-chip");
    if (chip) {
      e.dataTransfer.setData("text/plain", chip.getAttribute("data-tag"));
      e.dataTransfer.effectAllowed = "move";
      chip.classList.add("dragging");
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const tagValue = e.dataTransfer.getData("text/plain");
    if (!tagValue || !editorRef.current) return;

    // Remove the dragged chip from its original position
    const dragging = editorRef.current.querySelector(".email-tag-chip.dragging");
    if (dragging) {
      const zwsp = dragging.nextSibling;
      dragging.parentNode.removeChild(dragging);
      if (zwsp && zwsp.nodeType === Node.TEXT_NODE && zwsp.textContent === "\u200B") {
        zwsp.parentNode.removeChild(zwsp);
      }
    }

    // Insert at drop position using the caretPositionFromPoint
    let range = null;
    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }

    if (range && editorRef.current.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      const chipHtml = buildChipHtml(tagValue) + "\u200B";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = chipHtml;
      const frag = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        frag.appendChild(tempDiv.firstChild);
      }
      range.insertNode(frag);
    }

    emitChange();
  }, [emitChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnd = useCallback(() => {
    const dragging = editorRef.current?.querySelector(".email-tag-chip.dragging");
    if (dragging) dragging.classList.remove("dragging");
  }, []);

  return (
    <div className={clsx("template-container", classes.templateContainer)}>
      <div
        className={clsx("template-body", classes.templateBody)}
        onClick={handleContainerClick}
        data-id={testId}
        data-testid={testId}
      >
        <span
          className={`${classes.templateLabel} ${focused ? classes.templateLabelFocused : ""}`}
        >
          {label}
        </span>
        <div
          ref={editorRef}
          className={classes.editableArea}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          data-testid={`${testId}-editable`}
        />
      </div>
    </div>
  );
});

TemplateBodyField.displayName = "TemplateBodyField";

const EmailTemplateEditor = ({ title, subjectFieldName, bodyFieldName, control }) => {
  const classes = useEmailTemplateStyles();
  const { setValue, getValues, trigger } = useFormContext();
  const bodyEditorRef = useRef(null);

  const handleInsertTag = useCallback(
    (tagValue) => {
      const editor = bodyEditorRef.current?.getEditorElement();
      if (editor) {
        editor.focus();
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          // Only insert if cursor is inside the editor
          if (editor.contains(range.startContainer)) {
            const chipHtml = buildChipHtml(tagValue) + "\u200B";
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = chipHtml;
            const frag = document.createDocumentFragment();
            let lastNode;
            while (tempDiv.firstChild) {
              lastNode = frag.appendChild(tempDiv.firstChild);
            }
            range.deleteContents();
            range.insertNode(frag);
            // Move cursor after the inserted chip
            if (lastNode) {
              range.setStartAfter(lastNode);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            }
            // Trigger change — mark as internal so useEffect won't reset cursor
            bodyEditorRef.current.markInternalChange();
            const val = htmlToValue(editor).replace(/^\n+$/, "");
            setValue(bodyFieldName, val, { shouldDirty: true });
            trigger(bodyFieldName);
            return;
          }
        }
      }
      // Fallback: append at end if no cursor in editor
      const currentValue = getValues(bodyFieldName) || "";
      const newValue = currentValue + tagValue;
      setValue(bodyFieldName, newValue, { shouldDirty: true });
      trigger(bodyFieldName);
    },
    [bodyFieldName, setValue, getValues, trigger]
  );

  return (
    <div className={clsx("email-template-editor")} data-id={`email-template-${bodyFieldName}`} data-testid={`email-template-${bodyFieldName}`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name={subjectFieldName}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={`${title} Subject`}
                variant="outlined"
                fullWidth
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                data-testid={`${subjectFieldName}-input`}
                inputProps={{ "data-testid": `${subjectFieldName}` }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={bodyFieldName}
            control={control}
            render={({ field }) => (
              <TemplateBodyField
                ref={bodyEditorRef}
                value={field.value}
                onChange={field.onChange}
                label={`${title} Template`}
                testId={`${bodyFieldName}-input`}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="body2">Tags:</Typography>
            </Grid>
            {EMAIL_TAGS.map((tag) => (
              <Grid item key={tag.value}>
                <Button
                  variant="contained"
                  size="small"
                  className={clsx("email-tags", classes.tagButton)}
                  onClick={() => handleInsertTag(tag.value)}
                  data-id={`tag-btn-${tag.label}`}
                  data-testid={`tag-btn-${tag.label}`}
                  title={tag.description}
                >
                  {tag.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

EmailTemplateEditor.propTypes = {
  title: PropTypes.string.isRequired,
  subjectFieldName: PropTypes.string.isRequired,
  bodyFieldName: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
};

export default EmailTemplateEditor;
